/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useMemo } from 'react';

import { useParams } from 'js/lib/useRouter';

import { Grid, Link, PageGridContainer, Typography2 } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';

import type { CoreCollectionEntityWithMetadata } from 'bundles/collections-carousel/types/CarouselCollection';
import { mapCollectionEntityForCarousel } from 'bundles/collections-carousel/utils/mappingUtils';
import CollectionModule from 'bundles/page-config-common/components/shared/CollectionModule';
import HomeSection from 'bundles/page-config-common/components/shared/HomeSection';
import { useHeaderLevel } from 'bundles/page-config-common/contexts/HeaderLevelContext';
import type { PageConfig_ComponentConfig_PageConfig_EnterpriseBrowseCollectionComponent_Fragment as CollectionComponent } from 'bundles/page-config-common/queries/page-config/__generated__/PageConfigComponentsQuery';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';
import { ENTERPRISE_BROWSE_COLLECTION_LIMIT } from 'bundles/unified-home-common/constants';

import _t from 'i18n!nls/consumer-home';

const styles = {
  enterpriseCollectionHeader: css`
    margin-top: var(--cds-spacing-150);
  `,
  header: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
  `,
  button: css`
    padding-left: 0;
    padding-right: 0;
  `,
};

const FREE_COLLECTION_IDS = ['LmyL-2WZTmeRq-rWx2tDVw'];

const EnterpriseBrowseCollectionModuleRenderer = ({
  pageConfigComponent,
}: {
  pageConfigComponent: CollectionComponent;
}) => {
  const { programSlug } = useParams();
  const { currentLevel, nextLevel } = useHeaderLevel();
  const carouselCollections = useMemo(() => {
    return (
      pageConfigComponent.collection?.enterpriseBrowseCollectionEntities?.elements
        // Filter out "free" collections.
        ?.filter(
          (collection) =>
            collection?.__typename === 'Collection_Collection' && !FREE_COLLECTION_IDS.includes(collection.id)
        )
        ?.slice(0, ENTERPRISE_BROWSE_COLLECTION_LIMIT)
        ?.map((maybeCollection, index) => {
          if (maybeCollection?.__typename === 'Collection_Collection') {
            return mapCollectionEntityForCarousel(
              maybeCollection?.id,
              {
                ...maybeCollection,
                metadata: {
                  isEnterprise: true,
                  programSlug,
                  titleComponent: nextLevel,
                },
              } as CoreCollectionEntityWithMetadata,
              {
                collectionIndex: index,
              }
            );
          } else {
            return null;
          }
        })
        ?.filter(Boolean)
    );
  }, [pageConfigComponent, programSlug, nextLevel]);

  if (!carouselCollections || !carouselCollections.length) {
    return null;
  }

  return (
    <HomeSection className="PageConfig_EnterpriseBrowseCollectionComponent" dataTestId="enterprise-browse-collections">
      {carouselCollections?.map((browseCollection) => (
        <CollectionModule
          key={browseCollection.collectionId}
          collection={browseCollection}
          customHeader={
            <div css={styles.enterpriseCollectionHeader}>
              <Typography2 variant="titleSmall" component={currentLevel}>
                {browseCollection.title}
              </Typography2>
            </div>
          }
        />
      ))}
      <PageGridContainer>
        <Grid item xs={12}>
          <Typography2 component="p" variant="subtitleLarge">
            {_t('Want to see more? ')}
            <Link
              component={TrackedLink2}
              variant="quiet"
              data={{ programSlug }}
              trackingName="browse_collections_footer_view_all_arrow_link"
              href={`/programs/${programSlug}/view-all-collections/browse`}
            >
              {_t('View all recommendations from Coursera')}{' '}
              <ArrowNextIcon size="medium" style={{ verticalAlign: 'middle' }} />
            </Link>
          </Typography2>
        </Grid>
      </PageGridContainer>
    </HomeSection>
  );
};

export default EnterpriseBrowseCollectionModuleRenderer;
